import { createSlice } from "@reduxjs/toolkit";
import html2canvas from "html2canvas";
import { jsPDF as JsPDF } from "jspdf";
import "jspdf-autotable";

import {
  executeListLoadDataEachProductDailies,
  executeListLoadDataEachProductLastMonths,
  executeListLoadDataEachShotDailies,
  executeListLoadDataEachShotLastMonths,
} from "../api/DataGraphApi";
import { storeKeyName } from "../common/AppConstant";
import { handleError } from "../common/ErrorUtil";
import { ipaexg } from "../font/ipaexg00401/ipaexg-normal";
import { graphType } from "../model/Common";
import {
  chartType,
  dataGraphSelectLabel,
  dataTableLabel,
  languageType,
  rangeType,
} from "../model/DataGraph";

// ソート処理
const executeSort = (data, column, order) => {
  const sortRule = { asc: [1, -1], desc: [-1, 1] };

  const sortData = data.sort((a, b) => {
    if (a[column] > b[column]) {
      return sortRule[order][0];
    }
    if (a[column] < b[column]) {
      return sortRule[order][1];
    }
    return 0;
  });
  return sortData;
};

/**
 * Slice.
 *
 * @type {object}
 */
export const slice = createSlice({
  // Slice名
  name: storeKeyName.dataGraph,

  // 初期State
  initialState: {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: false,
    // PDF生成中フラグ（true: 表示、false: 非表示）
    isCreating: false,
    // PDF出力言語
    language: null,
    // エラー内容
    error: null,
    // 製品単位データ一覧
    productDatas: {
      daily: {
        x: [],
        y: [],
      },
      month: {
        x: [],
        y: [],
      },
    },
    // Shot数単位データ一覧
    shotDatas: {
      daily: {
        x: [],
        y: [],
      },
      month: {
        x: [],
        y: [],
      },
    },
    // Shot数単位エリアデータ一覧
    shotAreaDatas: {
      daily: {
        x: [],
        y: [],
      },
      month: {
        x: [],
        y: [],
      },
    },
  },

  // Reducer
  reducers: {
    // 通信開始
    fetchStart(state) {
      state.isLoading = true;
    },
    // 通信失敗
    fetchFailure(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    // 通信成功
    fetchSuccess(state) {
      state.error = null;
      state.isLoading = false;
    },
    // PDF生成開始
    fetchCreateStart(state, action) {
      state.language = action.payload;
      state.isCreating = true;
    },
    // PDF生成失敗
    fetchCreateFailure(state, action) {
      state.error = action.payload;
      state.language = null;
      state.isCreating = false;
    },
    // PDF生成成功
    fetchCreateSuccess(state) {
      state.error = null;
      state.language = null;
      state.isCreating = false;
    },
    // クリア
    fetchClear(state) {
      state.error = null;
      state.language = null;
      state.productDatas.daily = { x: [], y: [] };
      state.productDatas.month = { x: [], y: [] };
      state.shotDatas.daily = { x: [], y: [] };
      state.shotDatas.month = { x: [], y: [] };
      state.shotAreaDatas.daily = { x: [], y: [] };
      state.shotAreaDatas.month = { x: [], y: [] };
      state.isLoading = false;
      state.isCreating = false;
    },
    // 製品単位データ一覧の更新(日単位)
    fetchUpdateProductDailies(state, action) {
      state.productDatas.daily = { x: [], y: [] };

      // ソート条件
      const column = "productId";
      const order = "asc";

      // ソートしたデータをStoreに格納
      const sortData = executeSort([...action.payload], column, order);
      sortData.forEach((data) => {
        state.productDatas.daily.x.push({
          productId: data.productId,
          dataCount: data.dataCount,
          eccentricity: data.eccentricityX,
          totalLoad: data.totalLoad,
        });
        state.productDatas.daily.y.push({
          productId: data.productId,
          dataCount: data.dataCount,
          eccentricity: data.eccentricityY,
          totalLoad: data.totalLoad,
        });
      });
    },
    // 製品単位データ一覧の更新(月単位)
    fetchUpdateProductLastMonths(state, action) {
      state.productDatas.month = { x: [], y: [] };

      // ソート条件
      const column = "productId";
      const order = "asc";

      // ソートしたデータをStoreに格納
      const sortData = executeSort([...action.payload], column, order);
      sortData.forEach((data) => {
        state.productDatas.month.x.push({
          productId: data.productId,
          dataCount: data.dataCount,
          eccentricity: data.eccentricityX,
          totalLoad: data.totalLoad,
        });
        state.productDatas.month.y.push({
          productId: data.productId,
          dataCount: data.dataCount,
          eccentricity: data.eccentricityY,
          totalLoad: data.totalLoad,
        });
      });
    },
    // Shot数単位データ一覧の更新(日単位)
    fetchUpdateShotDailies(state, action) {
      state.shotDatas.daily = { x: [], y: [] };

      action.payload.forEach((record) => {
        const distributionData = [];
        record.loadDistributionData.forEach((data) => {
          // 計測データ展開
          distributionData.push({
            eccentricity: data[0],
            totalLoad: data[1],
            dataCount: data[2],
          });

          // Shot数で降順ソート
          const sortDistributionData = executeSort(
            [...distributionData],
            "dataCount",
            "desc"
          );

          // データタイプに応じてStoreに格納
          if (record.type === "X") {
            state.shotDatas.daily.x = sortDistributionData;
          } else if (record.type === "Y") {
            state.shotDatas.daily.y = sortDistributionData;
          }
        });
      });

      // Shot数単位エリアデータ一覧の更新(日単位)
      state.shotAreaDatas.daily = { x: [], y: [] };

      state.shotDatas.daily.x.forEach((data) => {
        state.shotAreaDatas.daily.x.push({
          dataCount: data.dataCount,
          x1: data.eccentricity,
          y1: data.totalLoad,
          x2:
            data.eccentricity >= 0
              ? data.eccentricity - 50
              : data.eccentricity + 50,
          y2: data.totalLoad >= 0 ? data.totalLoad - 500 : data.totalLoad + 500,
        });
      });

      state.shotDatas.daily.y.forEach((data) => {
        state.shotAreaDatas.daily.y.push({
          dataCount: data.dataCount,
          x1: data.eccentricity,
          y1: data.totalLoad,
          x2:
            data.eccentricity >= 0
              ? data.eccentricity - 50
              : data.eccentricity + 50,
          y2: data.totalLoad >= 0 ? data.totalLoad - 500 : data.totalLoad + 500,
        });
      });
    },
    // Shot数単位データ一覧の更新(月単位)
    fetchUpdateShotLastMonths(state, action) {
      state.shotDatas.month = { x: [], y: [] };

      action.payload.forEach((record) => {
        const distributionData = [];
        record.loadDistributionData.forEach((data) => {
          // 計測データ展開
          distributionData.push({
            eccentricity: data[0],
            totalLoad: data[1],
            dataCount: data[2],
          });

          // Shot数で降順ソート
          const sortDistributionData = executeSort(
            [...distributionData],
            "dataCount",
            "desc"
          );

          // データタイプに応じてStoreに格納
          if (record.type === "X") {
            state.shotDatas.month.x = sortDistributionData;
          } else if (record.type === "Y") {
            state.shotDatas.month.y = sortDistributionData;
          }
        });
      });

      // Shot数単位エリアデータ一覧の更新(月単位)
      state.shotAreaDatas.month = { x: [], y: [] };

      state.shotDatas.month.x.forEach((data) => {
        state.shotAreaDatas.month.x.push({
          dataCount: data.dataCount,
          x1: data.eccentricity,
          y1: data.totalLoad,
          x2:
            data.eccentricity >= 0
              ? data.eccentricity - 50
              : data.eccentricity + 50,
          y2: data.totalLoad >= 0 ? data.totalLoad - 500 : data.totalLoad + 500,
        });
      });

      state.shotDatas.month.y.forEach((data) => {
        state.shotAreaDatas.month.y.push({
          dataCount: data.dataCount,
          x1: data.eccentricity,
          y1: data.totalLoad,
          x2:
            data.eccentricity >= 0
              ? data.eccentricity - 50
              : data.eccentricity + 50,
          y2: data.totalLoad >= 0 ? data.totalLoad - 500 : data.totalLoad + 500,
        });
      });
    },
    fetchCreatePDFData(state, action) {
      // PDF作成 (デフォルト設定はA4縦)
      const pdf = new JsPDF();

      // 日本語フォントをbase64変換して使用可能にする
      pdf.addFileToVFS("ipaexg.ttf", ipaexg);
      pdf.addFont("ipaexg.ttf", "ipaexg", "normal");
      pdf.setFont("ipaexg", "normal");

      // コンテンツ生成 (1ページ目)
      // 選択項目情報
      const margin = 10;
      const separation = "：　";
      const labelOffice = dataGraphSelectLabel.Customer[state.language];
      const labelDate = dataGraphSelectLabel.Date[state.language];
      const labelRange = dataGraphSelectLabel.Range[state.language];
      const labelGraph = dataGraphSelectLabel.DisplayItem[state.language];
      const valueRange = action.payload.range.label[state.language];
      const valueGraph = action.payload.graph.label[state.language];
      let labelMargin = 0;

      if (languageType.ja === state.language) {
        labelMargin = margin + 35;
      } else if (languageType.en === state.language) {
        labelMargin = margin + 65;
      }
      pdf.text(labelOffice, margin, margin);
      pdf.text(labelDate, margin, margin * 2);
      pdf.text(labelRange, margin, margin * 3);
      pdf.text(labelGraph, margin, margin * 4);
      pdf.text(`${separation}${action.payload.office}`, labelMargin, margin);
      pdf.text(`${separation}${action.payload.date}`, labelMargin, margin * 2);
      pdf.text(`${separation}${valueRange}`, labelMargin, margin * 3);
      pdf.text(`${separation}${valueGraph}`, labelMargin, margin * 4);

      // グラフ情報
      pdf.addImage(action.payload.chartX, "JPEG", 5, margin * 5, 100, 100);
      pdf.addImage(action.payload.chartY, "JPEG", 105, margin * 5, 100, 100);

      // コンテンツ生成 (2ページ目)
      pdf.addPage();
      // データ一覧
      let xHead;
      let yHead;
      let xBody;
      let yBody;
      if (action.payload.graph.value === chartType[1].value) {
        // 荷重 - 製品単位データグラフの場合
        xHead = [
          [
            dataTableLabel.partNo[state.language],
            dataTableLabel.shot[state.language],
            dataTableLabel.eccentricity[state.language](graphType.lr),
            dataTableLabel.load[state.language],
          ],
        ];
        yHead = [
          [
            dataTableLabel.partNo[state.language],
            dataTableLabel.shot[state.language],
            dataTableLabel.eccentricity[state.language](graphType.fb),
            dataTableLabel.load[state.language],
          ],
        ];

        if (action.payload.range.value === rangeType[1].value) {
          // 集計範囲が当日の場合
          if (state.productDatas.daily.x.length) {
            xBody = state.productDatas.daily.x.map((data) => [
              data.productId,
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            xBody = [["-", "-", "-", "-"]];
          }
          if (state.productDatas.daily.y.length) {
            yBody = state.productDatas.daily.y.map((data) => [
              data.productId,
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            yBody = [["-", "-", "-", "-"]];
          }
        } else if (action.payload.range.value === rangeType[0].value) {
          // 集計範囲が1ヶ月の場合
          if (state.productDatas.month.x.length) {
            xBody = state.productDatas.month.x.map((data) => [
              data.productId,
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            xBody = [["-", "-", "-", "-"]];
          }
          if (state.productDatas.month.y.length) {
            yBody = state.productDatas.month.y.map((data) => [
              data.productId,
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            yBody = [["-", "-", "-", "-"]];
          }
        } else {
          xBody = [["-", "-", "-", "-"]];
          yBody = [["-", "-", "-", "-"]];
        }
      } else if (action.payload.graph.value === chartType[0].value) {
        // 荷重 - Shot数単位データグラフの場合
        xHead = [
          [
            dataTableLabel.shot[state.language],
            dataTableLabel.eccentricity[state.language](graphType.lr),
            dataTableLabel.load[state.language],
          ],
        ];
        yHead = [
          [
            dataTableLabel.shot[state.language],
            dataTableLabel.eccentricity[state.language](graphType.fb),
            dataTableLabel.load[state.language],
          ],
        ];

        if (action.payload.range.value === rangeType[1].value) {
          // 集計範囲が当日の場合
          const shotDailyX = state.shotDatas.daily.x.filter(
            (data) => data.dataCount > 100
          );
          const shotDailyY = state.shotDatas.daily.y.filter(
            (data) => data.dataCount > 100
          );
          if (shotDailyX.length) {
            xBody = shotDailyX.map((data) => [
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            xBody = [["-", "-", "-"]];
          }
          if (shotDailyY.length) {
            yBody = shotDailyY.map((data) => [
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            yBody = [["-", "-", "-"]];
          }
        } else if (action.payload.range.value === rangeType[0].value) {
          // 集計範囲が1ヶ月の場合
          const shotMonthX = state.shotDatas.month.x.filter(
            (data) => data.dataCount > 100
          );
          const shotMonthY = state.shotDatas.month.y.filter(
            (data) => data.dataCount > 100
          );
          if (shotMonthX.length) {
            xBody = shotMonthX.map((data) => [
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            xBody = [["-", "-", "-"]];
          }
          if (shotMonthY.length) {
            yBody = shotMonthY.map((data) => [
              data.dataCount,
              data.eccentricity,
              data.totalLoad,
            ]);
          } else {
            yBody = [["-", "-", "-"]];
          }
        } else {
          xBody = [["-", "-", "-"]];
          yBody = [["-", "-", "-"]];
        }
      }
      // A4サイズの横幅は210mm
      // 右マージン5mm、左マージン107mmを設定しているので
      // テーブル描画の横幅は210-5-107=98mm
      // カラムの横幅の合計が98になるように設定
      const columnStyleShot = {
        0: { cellWidth: 18 }, // Shot数の列幅
        1: { cellWidth: 50 }, // 偏心量の列幅
        2: { cellWidth: 30 }, // 合計荷重（平均）の列幅
      };

      const columnStyleProduct = {
        0: { cellWidth: 18 }, // 製品番号の列幅
        1: { cellWidth: 18 }, // Shot数の列幅
        2: { cellWidth: 40 }, // 偏心量の列幅
        3: { cellWidth: 22 }, // 合計荷重（平均）の列幅
      };

      pdf.autoTable({
        theme: "grid",
        styles: {
          font: "ipaexg",
          fontStyle: "normal",
          fontSize: 7.3,
          halign: "center",
          valign: "middle",
          cellPadding: { top: 0.5, bottom: 0.5 },
        },
        headStyles: { fillColor: "#3f51b5" },
        margin: { top: 5, left: 5, right: 107, bottom: 5 },
        startY: 5,
        head: xHead,
        body: xBody,
        columnStyles:
          action.payload.graph.value === chartType[0].value
            ? columnStyleShot
            : columnStyleProduct,
      });

      pdf.setPage(2);
      pdf.autoTable({
        theme: "grid",
        styles: {
          font: "ipaexg",
          fontStyle: "normal",
          fontSize: 7.3,
          halign: "center",
          valign: "middle",
          cellPadding: { top: 0.5, bottom: 0.5 },
        },
        headStyles: { fillColor: "#3f51b5" },
        margin: { top: 5, left: 107, right: 5, bottom: 5 },
        startY: 5,
        head: yHead,
        body: yBody,
        columnStyles:
          action.payload.graph.value === chartType[0].value
            ? columnStyleShot
            : columnStyleProduct,
      });

      // PDF出力
      let fileNameSuffix = "";
      if (languageType.ja === state.language) {
        fileNameSuffix = "_jpn";
      } else if (languageType.en === state.language) {
        fileNameSuffix = "_eng";
      }
      pdf.save(`${action.payload.fileName}${fileNameSuffix}.pdf`);
    },
  },
});

/**
 * Actions.
 *
 * @type {object}
 */
export const {
  fetchStart,
  fetchFailure,
  fetchSuccess,
  fetchCreateStart,
  fetchCreateFailure,
  fetchCreateSuccess,
  fetchClear,
  fetchUpdateProductDailies,
  fetchUpdateProductLastMonths,
  fetchUpdateShotDailies,
  fetchUpdateShotLastMonths,
  fetchCreatePDFData,
} = slice.actions;

/**
 * Selectors.
 *
 * @type {function}
 */
export const selector = (stateAll) =>
  // すべてのStoreのStateが含まれているため、特定のStoreのStateのみ返す
  stateAll[storeKeyName.dataGraph];

/**
 * fetchListProductDailies.
 *
 */
export const fetchListProductDailies = (office, date) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListLoadDataEachProductDailies(
    office,
    date
  ).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchUpdateProductDailies(data.productDailies));
  dispatch(fetchSuccess());
};

/**
 * fetchListProductLastMonths.
 *
 */
export const fetchListProductLastMonths = (office, date) => async (
  dispatch
) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListLoadDataEachProductLastMonths(
    office,
    date
  ).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchUpdateProductLastMonths(data.productLastMonths));
  dispatch(fetchSuccess());
};

/**
 * fetchListShotDailies.
 *
 */
export const fetchListShotDailies = (office, date) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListLoadDataEachShotDailies(
    office,
    date
  ).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchUpdateShotDailies(data.shotDailies));
  dispatch(fetchSuccess());
};

/**
 * fetchListShotLastMonths.
 *
 */
export const fetchListShotLastMonths = (office, date) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListLoadDataEachShotLastMonths(
    office,
    date
  ).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchUpdateShotLastMonths(data.shotLastMonths));
  dispatch(fetchSuccess());
};

/**
 * fetchCreatePDF.
 *
 */
export const fetchCreatePDF = (
  fileName,
  office,
  date,
  range,
  graph,
  refX,
  refY
  // xFunc,
  // yFunc
) => async (dispatch) => {
  await new Promise((resolve) => setTimeout(resolve, 100));

  // 表示グラフを画像化
  const chartX = await html2canvas(refX.current.container).then((canvas) =>
    canvas.toDataURL("image/jpeg", 1.0)
  );
  const chartY = await html2canvas(refY.current.container).then((canvas) =>
    canvas.toDataURL("image/jpeg", 1.0)
  );

  const payload = {
    fileName,
    office,
    date,
    range,
    graph,
    chartX,
    chartY,
  };

  dispatch(fetchCreatePDFData(payload));
  dispatch(fetchCreateSuccess());
};
